import { useEffect, useMemo, useRef, useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileSpreadsheet,
  FileText,
  Paperclip,
  Plus,
  Search,
} from "lucide-react";
import * as XLSX from "xlsx";

const novoCredito = () => ({
  origem: "destinatario" as "pedido" | "destinatario",
  pedidoId: null as number | null,
  destinatarioId: null as number | null,
  destinatarioNome: "",
  valor: "",
  descricao: "",
});

function formatCurrency(value: unknown) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value) || 0);
}

function formatDateTime(value: unknown) {
  if (!value) return "-";
  return new Date(String(value).includes("T") ? String(value) : `${value}T12:00:00`).toLocaleString("pt-BR");
}

function formatDate(value: unknown) {
  if (!value) return "-";
  return new Date(`${value}T12:00:00`).toLocaleDateString("pt-BR");
}

function statusLabel(status: string | null | undefined) {
  if (status === "pago") return "Pago";
  if (status === "excluido") return "Excluído";
  return "Pendente";
}

export default function CreditosDestinatarios() {
  const { user } = useGoogleAuth();
  const utils = trpc.useUtils();
  const [emitenteId, setEmitenteId] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showDestinatarios, setShowDestinatarios] = useState(false);
  const [destinatarioSearch, setDestinatarioSearch] = useState("");
  const [destinatarioPage, setDestinatarioPage] = useState(1);
  const [pedidoSearch, setPedidoSearch] = useState("");
  const [credito, setCredito] = useState(novoCredito());
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [showDetailId, setShowDetailId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();

  useEffect(() => {
    if (!emitenteId && user?.emitenteId) setEmitenteId(Number(user.emitenteId));
  }, [emitenteId, user?.emitenteId]);

  const queryInput = useMemo(() => ({
    emitenteId,
    search: search.trim() || undefined,
    dataDe: dataDe || undefined,
    dataAte: dataAte || undefined,
    page,
    pageSize: 20,
  }), [emitenteId, search, dataDe, dataAte, page]);

  const { data, isLoading, refetch } = trpc.creditosDestinatarios.list.useQuery(queryInput);
  const selectedEmitenteId = emitenteId;
  const destinatariosQuery = trpc.destinatarios.list.useQuery({
    emitenteId: selectedEmitenteId,
    page: destinatarioPage,
    limit: 20,
    search: destinatarioSearch.trim() || undefined,
  }, { enabled: showDestinatarios && !!selectedEmitenteId });
  const pedidosQuery = trpc.creditosDestinatarios.buscarPedidos.useQuery({
    emitenteId: selectedEmitenteId ?? 0,
    search: pedidoSearch.trim(),
  }, { enabled: showCreate && credito.origem === "pedido" && !!selectedEmitenteId && pedidoSearch.trim().length > 0 });

  const createMutation = trpc.creditosDestinatarios.create.useMutation();
  const detalheQuery = trpc.creditosDestinatarios.getById.useQuery(
    { id: showDetailId ?? 0 },
    { enabled: !!showDetailId }
  );
  const historicoQuery = trpc.creditosDestinatarios.historico.useQuery(
    { creditoId: showDetailId ?? 0 },
    { enabled: !!showDetailId }
  );
  const arquivosQuery = trpc.creditosDestinatarios.arquivos.useQuery(
    { creditoId: showDetailId ?? 0 },
    { enabled: !!showDetailId }
  );

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const totalCreditos = data?.totalCreditos ?? 0;
  const destinatarios = (destinatariosQuery.data as any)?.data ?? [];
  const destinatariosTotalPages = Number((destinatariosQuery.data as any)?.totalPages ?? 1);

  const abrirNovoCredito = () => {
    if (!selectedEmitenteId) {
      toast.error("Selecione o emitente antes de lançar o crédito.");
      return;
    }
    setCredito(novoCredito());
    setArquivos([]);
    setPedidoSearch("");
    setDestinatarioSearch("");
    setDestinatarioPage(1);
    setShowCreate(true);
  };

  const enviarAnexos = async (creditoId: number) => {
    if (!arquivos.length) return;
    const formData = new FormData();
    arquivos.forEach((arquivo) => formData.append("files", arquivo));
    const resposta = await fetch(`/api/creditos-destinatarios/${creditoId}/arquivos`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    const payload = await resposta.json().catch(() => ({}));
    if (!resposta.ok) throw new Error(payload.error || "Falha ao anexar os arquivos.");
  };

  const salvarCredito = async () => {
    if (!selectedEmitenteId) return toast.error("Emitente obrigatório.");
    if (!credito.destinatarioId) return toast.error("Selecione o destinatário ou um pedido.");
    const valor = Number(credito.valor);
    if (!Number.isFinite(valor) || valor <= 0) return toast.error("Informe um valor maior que zero.");
    if (!credito.descricao.trim()) return toast.error("Informe a descrição do crédito.");

    try {
      const resultado = await createMutation.mutateAsync({
        emitenteId: selectedEmitenteId,
        destinatarioId: credito.destinatarioId,
        pedidoId: credito.origem === "pedido" ? credito.pedidoId : null,
        valor,
        descricao: credito.descricao.trim(),
      });
      try {
        await enviarAnexos(resultado.id);
        toast.success("Crédito lançado e registrado em Contas a Receber.");
      } catch (error: any) {
        toast.error(`Crédito lançado, mas os anexos não foram salvos: ${error.message}`);
      }
      setShowCreate(false);
      setCredito(novoCredito());
      setArquivos([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await refetch();
    } catch (error: any) {
      toast.error(error.message || "Não foi possível lançar o crédito.");
    }
  };

  const carregarTodosParaRelatorio = async () => {
    const pageSize = 1000;
    const primeira = await utils.creditosDestinatarios.list.fetch({ ...queryInput, page: 1, pageSize });
    const todas = [...(primeira?.rows ?? [])];
    for (let currentPage = 2; currentPage <= (primeira?.totalPages ?? 1); currentPage += 1) {
      const parte = await utils.creditosDestinatarios.list.fetch({ ...queryInput, page: currentPage, pageSize });
      todas.push(...(parte?.rows ?? []));
    }
    return todas;
  };

  const exportarExcel = async () => {
    if (!total) return toast.info("Nenhum crédito encontrado para os filtros atuais.");
    setExporting(true);
    try {
      const exportRows = await carregarTodosParaRelatorio();
      const worksheet = XLSX.utils.aoa_to_sheet([
        ["ID", "Emitente", "Destinatário", "Pedido", "Descrição", "Valor", "Data do Lançamento", "Conta a Receber", "Status", "Criado por", "Anexos"],
        ...exportRows.map((row: any) => [
          row.id,
          row.emitenteNome ?? "",
          row.destinatarioFantasia || row.destinatarioNome || "",
          row.pedidoId ?? "",
          row.descricao ?? "",
          Number(row.valor) || 0,
          row.criadoEm ? new Date(row.criadoEm) : "",
          row.contaReceberId ?? "",
          statusLabel(row.contaReceberStatus),
          row.criadoPorNome ?? "",
          Number(row.quantidadeArquivos) || 0,
        ]),
      ]);
      for (let index = 2; index <= exportRows.length + 1; index += 1) {
        if (worksheet[`F${index}`]) worksheet[`F${index}`].z = '[$R$-416] #,##0.00';
        if (worksheet[`G${index}`]) worksheet[`G${index}`].z = "dd/mm/yyyy hh:mm";
      }
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Créditos de Destinatários");
      XLSX.writeFile(workbook, "creditos_destinatarios.xlsx");
      toast.success("Relatório Excel exportado.");
    } catch (error: any) {
      toast.error(error.message || "Não foi possível exportar o relatório.");
    } finally {
      setExporting(false);
    }
  };

  const exportarPdf = async () => {
    if (!total) return toast.info("Nenhum crédito encontrado para os filtros atuais.");
    setExporting(true);
    try {
      const exportRows = await carregarTodosParaRelatorio();
      const jsPDF = (await import("jspdf")).default;
      const autoTable = (await import("jspdf-autotable")).default;
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      doc.setFontSize(15);
      doc.setFont("helvetica", "bold");
      doc.text("CRÉDITOS DE DESTINATÁRIOS", 148.5, 14, { align: "center" });
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(`${exportRows.length} registro(s)`, 14, 22);
      doc.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, 283, 22, { align: "right" });
      autoTable(doc, {
        startY: 27,
        head: [["ID", "Emitente", "Destinatário", "Pedido", "Descrição", "Valor", "Lançamento", "Status", "Anexos"]],
        body: exportRows.map((row: any) => [
          row.id,
          row.emitenteNome ?? "-",
          row.destinatarioFantasia || row.destinatarioNome || "-",
          row.pedidoId ?? "-",
          row.descricao ?? "-",
          formatCurrency(row.valor),
          formatDateTime(row.criadoEm),
          statusLabel(row.contaReceberStatus),
          row.quantidadeArquivos ?? 0,
        ]),
        styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
        headStyles: { fillColor: [31, 41, 55], textColor: 255, fontStyle: "bold" },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 36 },
          2: { cellWidth: 40 },
          3: { cellWidth: 16 },
          4: { cellWidth: 66 },
          5: { cellWidth: 22, halign: "right" },
          6: { cellWidth: 26 },
          7: { cellWidth: 20 },
          8: { cellWidth: 14, halign: "center" },
        },
      });
      doc.save("creditos_destinatarios.pdf");
      toast.success("Relatório PDF exportado.");
    } catch (error: any) {
      toast.error(error.message || "Não foi possível exportar o relatório.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crédito de Destinatários</h1>
          <p className="text-sm text-gray-500">Cada lançamento cria uma conta a receber negativa na data do lançamento.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={exporting} onClick={exportarPdf}><FileText className="w-4 h-4 mr-1" /> PDF</Button>
          <Button variant="outline" size="sm" disabled={exporting} onClick={exportarExcel}><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
          <Button size="sm" onClick={abrirNovoCredito}><Plus className="w-4 h-4 mr-1" /> Lançar crédito</Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div>
              <Label>Emitente</Label>
              <Select value={emitenteId ? String(emitenteId) : "todos"} onValueChange={(value) => { setEmitenteId(value === "todos" ? undefined : Number(value)); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  {user?.role === "admin" && <SelectItem value="todos">Todos os emitentes</SelectItem>}
                  {emitentes.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.nomeFantasia || emitente.razaoSocial}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Busca geral</Label>
              <Input placeholder="ID, pedido, destinatário, emitente ou texto" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
            </div>
            <div>
              <Label>Data de lançamento — de</Label>
              <Input type="date" value={dataDe} onChange={(event) => { setDataDe(event.target.value); setPage(1); }} />
            </div>
            <div>
              <Label>Data de lançamento — até</Label>
              <Input type="date" value={dataAte} onChange={(event) => { setDataAte(event.target.value); setPage(1); }} />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => { setSearch(""); setDataDe(""); setDataAte(""); setPage(1); }}><Search className="mr-1 h-4 w-4" /> Limpar filtros</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <CreditCard className="h-8 w-8 text-blue-600" />
            <div><p className="text-sm text-gray-500">Créditos encontrados</p><p className="text-xl font-bold">{total}</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <CreditCard className="h-8 w-8 text-green-600" />
            <div><p className="text-sm text-gray-500">Valor total dos créditos</p><p className="text-xl font-bold text-green-700">{formatCurrency(totalCreditos)}</p></div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">{total} registro{total === 1 ? "" : "s"}</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-gray-600">
                <tr>
                  <th className="p-2">ID</th><th className="p-2">Emitente</th><th className="p-2">Destinatário</th><th className="p-2">Pedido</th><th className="p-2">Descrição</th><th className="p-2 text-right">Valor</th><th className="p-2">Data</th><th className="p-2">Conta a Receber</th><th className="p-2">Anexos</th><th className="p-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? <tr><td colSpan={10} className="p-8 text-center text-gray-500">Carregando...</td></tr> : rows.length === 0 ? <tr><td colSpan={10} className="p-8 text-center text-gray-500">Nenhum crédito encontrado.</td></tr> : rows.map((row: any) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">#{row.id}</td>
                    <td className="p-2">{row.emitenteNome}</td>
                    <td className="p-2 font-medium">{row.destinatarioFantasia || row.destinatarioNome}</td>
                    <td className="p-2">{row.pedidoId ? `#${row.pedidoId}` : "-"}</td>
                    <td className="max-w-md p-2"><span className="line-clamp-2">{row.descricao}</span></td>
                    <td className="p-2 text-right font-medium text-green-700">{formatCurrency(row.valor)}</td>
                    <td className="p-2">{formatDateTime(row.criadoEm)}</td>
                    <td className="p-2"><span className="rounded-full bg-gray-100 px-2 py-1 text-xs">#{row.contaReceberId} · {statusLabel(row.contaReceberStatus)}</span></td>
                    <td className="p-2 text-center">{row.quantidadeArquivos > 0 ? <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> {row.quantidadeArquivos}</span> : "-"}</td>
                    <td className="p-2"><Button variant="outline" size="sm" onClick={() => setShowDetailId(Number(row.id))}>Detalhes</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && <div className="mt-4 flex items-center justify-between"><p className="text-sm text-gray-500">Página {page} de {totalPages}</p><div className="flex gap-2"><Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}><ChevronLeft className="h-4 w-4" /></Button><Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}><ChevronRight className="h-4 w-4" /></Button></div></div>}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-5xl overflow-y-auto">
          <DialogHeader><DialogTitle>Lançar crédito de destinatário</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="space-y-4">
              <div><Label>Emitente</Label><Input readOnly value={emitentes.find((emitente: any) => Number(emitente.id) === selectedEmitenteId)?.nomeFantasia || emitentes.find((emitente: any) => Number(emitente.id) === selectedEmitenteId)?.razaoSocial || ""} /></div>
              <div><Label>Vincular por</Label><Select value={credito.origem} onValueChange={(value) => { setCredito((current) => ({ ...novoCredito(), origem: value as "pedido" | "destinatario" })); setPedidoSearch(""); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="destinatario">Destinatário</SelectItem><SelectItem value="pedido">Pedido</SelectItem></SelectContent></Select></div>
              {credito.origem === "destinatario" ? <div><Label>Destinatário *</Label><Button variant="outline" className="w-full justify-start font-normal" onClick={() => { setDestinatarioSearch(""); setDestinatarioPage(1); setShowDestinatarios(true); }}>{credito.destinatarioNome || "Selecionar destinatário"}</Button></div> : <div className="space-y-2"><Label>Pedido *</Label><Input placeholder="Buscar por número do pedido ou destinatário" value={pedidoSearch} onChange={(event) => setPedidoSearch(event.target.value)} />{pedidoSearch.trim().length > 0 && <div className="max-h-52 overflow-y-auto rounded border">{pedidosQuery.isLoading ? <p className="p-3 text-sm text-gray-500">Buscando...</p> : (pedidosQuery.data ?? []).map((pedido: any) => <button key={pedido.id} type="button" className="block w-full border-b p-3 text-left text-sm hover:bg-muted" onClick={() => { setCredito((current) => ({ ...current, pedidoId: Number(pedido.id), destinatarioId: Number(pedido.destinatarioId), destinatarioNome: pedido.destinatarioNome || `Destinatário #${pedido.destinatarioId}` })); setPedidoSearch(`#${pedido.id} — ${pedido.destinatarioNome}`); }}><strong>Pedido #{pedido.id}</strong><br /><span className="text-gray-500">{pedido.destinatarioNome} · {formatCurrency(pedido.valorTotal ?? pedido.valor)}</span></button>)}{!pedidosQuery.isLoading && (pedidosQuery.data ?? []).length === 0 && <p className="p-3 text-sm text-gray-500">Nenhum pedido encontrado.</p>}</div>}</div>}
              {credito.origem === "pedido" && credito.pedidoId && <div className="rounded border bg-muted/30 p-3 text-sm"><strong>Selecionado:</strong> Pedido #{credito.pedidoId}<br /><strong>Destinatário:</strong> {credito.destinatarioNome}</div>}
            </div>
            <div className="space-y-4">
              <div><Label>Valor do crédito *</Label><Input type="number" step="0.01" min="0.01" value={credito.valor} onChange={(event) => setCredito((current) => ({ ...current, valor: event.target.value }))} placeholder="0,00" /></div>
              <div><Label>Descrição / justificativa *</Label><textarea className="min-h-40 w-full rounded-md border px-3 py-2 text-sm" value={credito.descricao} onChange={(event) => setCredito((current) => ({ ...current, descricao: event.target.value }))} placeholder="Descreva o motivo do crédito." /></div>
              <div><Label>Anexos</Label><input ref={fileInputRef} type="file" multiple className="block w-full text-sm" onChange={(event) => setArquivos(Array.from(event.target.files ?? []))} />{arquivos.length > 0 && <p className="mt-1 text-xs text-gray-500">{arquivos.length} arquivo(s) selecionado(s).</p>}</div>
              <p className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">O lançamento será criado como valor negativo em Contas a Receber, com vencimento na data atual. As ações normais de Contas a Receber permanecem disponíveis.</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t pt-4"><Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button><Button disabled={createMutation.isPending} onClick={salvarCredito}>{createMutation.isPending ? "Salvando..." : "Lançar crédito"}</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDestinatarios} onOpenChange={setShowDestinatarios}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto">
          <DialogHeader><DialogTitle>Selecionar destinatário</DialogTitle></DialogHeader>
          <Input placeholder="Buscar por razão social, fantasia, CNPJ ou CPF" value={destinatarioSearch} onChange={(event) => { setDestinatarioSearch(event.target.value); setDestinatarioPage(1); }} />
          <div className="overflow-x-auto rounded border"><table className="w-full text-sm"><thead className="bg-muted"><tr><th className="p-3 text-left">Razão Social</th><th className="p-3 text-left">Fantasia</th><th className="p-3 text-left">CNPJ/CPF</th><th className="p-3">Ação</th></tr></thead><tbody>{destinatarios.map((destinatario: any) => <tr key={destinatario.id} className="border-t"><td className="p-3">{destinatario.razaoSocial || "-"}</td><td className="p-3">{destinatario.nomeFantasia || "-"}</td><td className="p-3">{destinatario.cnpj || destinatario.cpf || "-"}</td><td className="p-3 text-center"><Button size="sm" onClick={() => { setCredito((current) => ({ ...current, destinatarioId: Number(destinatario.id), destinatarioNome: destinatario.nomeFantasia || destinatario.razaoSocial || `Destinatário #${destinatario.id}` })); setShowDestinatarios(false); }}>Selecionar</Button></td></tr>)}{destinatarios.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-gray-500">Nenhum destinatário encontrado.</td></tr>}</tbody></table></div>
          {destinatariosTotalPages > 1 && <div className="flex justify-between"><Button variant="outline" size="sm" disabled={destinatarioPage <= 1} onClick={() => setDestinatarioPage((current) => current - 1)}>Anterior</Button><span className="text-sm text-gray-500">Página {destinatarioPage} de {destinatariosTotalPages}</span><Button variant="outline" size="sm" disabled={destinatarioPage >= destinatariosTotalPages} onClick={() => setDestinatarioPage((current) => current + 1)}>Próxima</Button></div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!showDetailId} onOpenChange={(open) => { if (!open) setShowDetailId(null); }}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-5xl overflow-y-auto">
          <DialogHeader><DialogTitle>Crédito #{showDetailId}</DialogTitle></DialogHeader>
          {detalheQuery.isLoading ? <p className="py-8 text-center text-gray-500">Carregando...</p> : detalheQuery.data && <div className="space-y-5"><div className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/20 p-4 md:grid-cols-3"><div><p className="text-xs text-gray-500">Emitente</p><p className="font-medium">{detalheQuery.data.emitenteNome}</p></div><div><p className="text-xs text-gray-500">Destinatário</p><p className="font-medium">{detalheQuery.data.destinatarioFantasia || detalheQuery.data.destinatarioNome}</p></div><div><p className="text-xs text-gray-500">Pedido</p><p className="font-medium">{detalheQuery.data.pedidoId ? `#${detalheQuery.data.pedidoId}` : "Sem pedido"}</p></div><div><p className="text-xs text-gray-500">Valor</p><p className="font-semibold text-green-700">{formatCurrency(detalheQuery.data.valor)}</p></div><div><p className="text-xs text-gray-500">Conta a Receber</p><p className="font-medium">#{detalheQuery.data.contaReceberId} · {statusLabel(detalheQuery.data.contaReceberStatus)}</p></div><div><p className="text-xs text-gray-500">Vencimento da conta</p><p className="font-medium">{formatDate(detalheQuery.data.dataVencimento)}</p></div></div><div><h3 className="mb-2 font-semibold">Descrição / justificativa</h3><div className="whitespace-pre-wrap rounded border p-4 text-sm">{detalheQuery.data.descricao}</div></div><div><h3 className="mb-2 font-semibold">Anexos</h3>{arquivosQuery.isLoading ? <p className="text-sm text-gray-500">Carregando...</p> : (arquivosQuery.data ?? []).length === 0 ? <p className="text-sm text-gray-500">Nenhum anexo.</p> : <div className="space-y-2">{(arquivosQuery.data ?? []).map((arquivo: any) => <a key={arquivo.id} href={arquivo.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded border p-3 text-sm hover:bg-muted"><span className="flex items-center gap-2"><Paperclip className="h-4 w-4" />{arquivo.nome}</span><span className="text-xs text-gray-500">{formatDateTime(arquivo.criadoEm)}</span></a>)}</div>}</div><div><h3 className="mb-2 font-semibold">Histórico</h3>{historicoQuery.isLoading ? <p className="text-sm text-gray-500">Carregando...</p> : (historicoQuery.data ?? []).length === 0 ? <p className="text-sm text-gray-500">Nenhum evento.</p> : <div className="space-y-2">{(historicoQuery.data ?? []).map((evento: any, index: number) => <div key={`${evento.origem}-${index}`} className="rounded border p-3 text-sm"><p>{evento.descricao}</p><p className="mt-1 text-xs text-gray-500">{evento.autorNome || "Sistema"} · {formatDateTime(evento.criadoEm)}</p></div>)}</div>}</div></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
